import { useNavigate } from 'react-router-dom';
import StatusBadge from './StatusBadge';
import { formatDate } from '../../utils/formatDate';
import { User, Phone, MapPin, Briefcase, Clock, BadgeCheck } from 'lucide-react';

const STATUS_THEMES = {
  approved: {
    accent: 'from-emerald-500 via-emerald-400 to-teal-400',
    ring: 'ring-emerald-200',
    photoBg: 'from-emerald-100 to-teal-100',
    photoIcon: 'text-emerald-600',
    hoverBorder: 'hover:border-emerald-300',
    footerBg: 'from-emerald-50 to-teal-50',
    footerIcon: 'text-emerald-600',
  },
  pending: {
    accent: 'from-amber-500 via-orange-400 to-yellow-400',
    ring: 'ring-amber-200',
    photoBg: 'from-amber-100 to-orange-100',
    photoIcon: 'text-amber-600',
    hoverBorder: 'hover:border-amber-300',
    footerBg: 'from-amber-50 to-orange-50',
    footerIcon: 'text-amber-600',
  },
  rejected: {
    accent: 'from-rose-500 via-red-400 to-pink-400',
    ring: 'ring-rose-200',
    photoBg: 'from-rose-100 to-pink-100',
    photoIcon: 'text-rose-600',
    hoverBorder: 'hover:border-rose-300',
    footerBg: 'from-rose-50 to-pink-50',
    footerIcon: 'text-rose-600',
  },
  default: {
    accent: 'from-ysrcp-600 via-ysrcp-500 to-ysrcp-400',
    ring: 'ring-ysrcp-200',
    photoBg: 'from-ysrcp-100 to-ysrcp-200',
    photoIcon: 'text-ysrcp-600',
    hoverBorder: 'hover:border-ysrcp-300',
    footerBg: 'from-ysrcp-50 to-blue-50',
    footerIcon: 'text-ysrcp-600',
  },
};

export default function RegistrationCard({ reg }) {
  const navigate = useNavigate();
  const theme = STATUS_THEMES[(reg.status || '').toLowerCase()] || STATUS_THEMES.default;

  return (
    <div
      onClick={() => navigate(`/registrations/${reg.id}`)}
      className={`group relative bg-white rounded-2xl border border-gray-100 overflow-hidden cursor-pointer shadow-card hover:shadow-card-hover ${theme.hoverBorder} transition-all duration-300 hover:-translate-y-0.5`}
    >
      {/* Top gradient accent bar */}
      <div className={`h-1.5 bg-gradient-to-r ${theme.accent}`} />

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="relative flex-shrink-0">
            <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${theme.photoBg} flex items-center justify-center overflow-hidden ring-2 ${theme.ring} shadow-sm`}>
              {reg.photo_url ? (
                <img src={reg.photo_url} alt={reg.full_name} className="w-full h-full object-cover" />
              ) : (
                <User className={`w-6 h-6 ${theme.photoIcon}`} />
              )}
            </div>
            {reg.status === 'approved' && (
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center ring-2 ring-white">
                <BadgeCheck className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-gray-900 text-sm truncate group-hover:text-ysrcp-600 transition-colors">
                {reg.full_name}
              </h4>
              <StatusBadge status={reg.status} />
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5 truncate font-mono">{reg.user_id}</p>
          </div>
        </div>

        {/* Details */}
        <div className="mt-3 space-y-1.5">
          {reg.committee_type && (
            <div className="flex items-center gap-2 text-xs bg-ysrcp-50/60 rounded-lg px-2.5 py-1.5">
              <Briefcase className="w-3.5 h-3.5 text-ysrcp-600 flex-shrink-0" />
              <span className="text-gray-700 truncate font-medium">
                {reg.committee_type}
                {reg.position && <span className="text-ysrcp-600"> — {reg.position}</span>}
              </span>
            </div>
          )}
          {(reg.mandal || reg.panchayat) && (
            <div className="flex items-center gap-2 text-xs bg-emerald-50/60 rounded-lg px-2.5 py-1.5">
              <MapPin className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
              <span className="text-gray-700 truncate">{[reg.mandal, reg.panchayat].filter(Boolean).join(', ')}</span>
            </div>
          )}
          {reg.phone_number && (
            <div className="flex items-center gap-2 text-xs bg-amber-50/60 rounded-lg px-2.5 py-1.5">
              <Phone className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
              <span className="text-gray-700 font-mono">{reg.phone_number}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`mt-3 -mx-4 -mb-4 px-4 py-2.5 bg-gradient-to-r ${theme.footerBg} border-t border-gray-100 flex items-center justify-between gap-2`}>
          <div className="flex items-center gap-1.5 text-[11px] text-gray-600 min-w-0">
            <Clock className={`w-3.5 h-3.5 ${theme.footerIcon} flex-shrink-0`} />
            <span className="truncate font-medium">{formatDate(reg.last_modified_date)}</span>
          </div>
          {reg.voter_id && (
            <span className="text-[10px] text-gray-600 font-mono bg-white/80 backdrop-blur px-2 py-0.5 rounded-full border border-white shadow-sm flex-shrink-0">
              {reg.voter_id}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
